import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { CONFIGURATION_RECORD_ACTIVE, CONFIGURATION_RECORD_INACTIVE } from '../constants/configuration';
import type {
  CreatePermissionConfigurationInput,
  CreateWfhConfigurationInput,
  PermissionConfiguration,
  PermissionOrgPolicy,
  PermissionOrgPolicyInput,
  UpdateWfhConfigurationInput,
  WfhConfiguration,
} from '../types/hrPolicyConfiguration';

function notesText(value: unknown): string {
  if (value == null) return '';
  return String(value);
}

type Envelope<T> = { success: boolean; message: string; data: T };
type Page<T> = {
  items: T[];
  pagination: { page: number; pageSize: number; totalRecords: number; totalPages: number };
};

const bool = (v: unknown) => Boolean(v);
const num = (v: unknown) => Number(v || 0);

function dateOnly(value: unknown): string {
  const text = value == null ? '' : String(value);
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(text);
  return match ? match[1] : text;
}

function wfh(v: WfhConfiguration): WfhConfiguration {
  return {
    ...v,
    wfhPolicyId: num(v.wfhPolicyId),
    description: v.description == null || String(v.description).trim() === '' ? null : String(v.description),
    effectiveFrom: dateOnly(v.effectiveFrom),
    maximumDaysPerMonth: num(v.maximumDaysPerMonth),
    maximumDaysPerYear: num(v.maximumDaysPerYear),
    maximumConsecutiveDays: num(v.maximumConsecutiveDays),
    minimumNoticeDays: num(v.minimumNoticeDays),
    allowHalfDay: bool(v.allowHalfDay),
    isDefault: bool(v.isDefault),
    isActive: bool(v.isActive),
    rowVersion: num(v.rowVersion),
  };
}

function permission(v: PermissionConfiguration): PermissionConfiguration {
  return {
    ...v,
    permissionTypeId: num(v.permissionTypeId),
    permissionPolicyId: num(v.permissionPolicyId),
    minimumDurationMinutes: num(v.minimumDurationMinutes),
    maximumDurationMinutes: num(v.maximumDurationMinutes),
    maximumPermissionsPerDay: num(v.maximumPermissionsPerDay),
    maximumPermissionsPerMonth: num(v.maximumPermissionsPerMonth),
    maximumPermissionsPerYear: num(v.maximumPermissionsPerYear),
    isDefault: bool(v.isDefault),
    isActive: bool(v.isActive),
    rowVersion: num(v.rowVersion),
  };
}

export async function getWfhConfigurations(
  search = '',
  filters?: { isActive?: boolean },
) {
  const r = await apiClient.get<Envelope<Page<WfhConfiguration>>>(
    API_ENDPOINTS.HR_POLICY_CONFIGURATIONS.WFH,
    {
      params: {
        search: search.trim() || undefined,
        page: 1,
        pageSize: 100,
        ...(filters?.isActive === true || filters?.isActive === false
          ? { isActive: filters.isActive }
          : { isActive: CONFIGURATION_RECORD_ACTIVE }),
      },
    },
  );
  return { ...r.data.data, items: r.data.data.items.map(wfh) };
}

export async function createWfhConfiguration(input: CreateWfhConfigurationInput) {
  const r = await apiClient.post<Envelope<WfhConfiguration>>(
    API_ENDPOINTS.HR_POLICY_CONFIGURATIONS.WFH,
    input,
  );
  return wfh(r.data.data);
}

export async function getWfhConfiguration(wfhPolicyId: number) {
  const r = await apiClient.get<Envelope<WfhConfiguration>>(
    API_ENDPOINTS.HR_POLICY_CONFIGURATIONS.UPDATE_WFH(wfhPolicyId),
  );
  return wfh(r.data.data);
}

export async function updateWfhConfiguration(
  wfhPolicyId: number,
  input: UpdateWfhConfigurationInput,
) {
  const r = await apiClient.put<Envelope<WfhConfiguration>>(
    API_ENDPOINTS.HR_POLICY_CONFIGURATIONS.UPDATE_WFH(wfhPolicyId),
    input,
  );
  return wfh(r.data.data);
}

export async function deactivateWfhConfiguration(item: WfhConfiguration) {
  const r = await apiClient.put<Envelope<WfhConfiguration>>(
    API_ENDPOINTS.HR_POLICY_CONFIGURATIONS.UPDATE_WFH(item.wfhPolicyId),
    {
      policyCode: item.policyCode,
      policyName: item.policyName,
      description: item.description,
      effectiveFrom: item.effectiveFrom,
      maximumDaysPerMonth: item.maximumDaysPerMonth,
      maximumDaysPerYear: item.maximumDaysPerYear,
      maximumConsecutiveDays: item.maximumConsecutiveDays,
      minimumNoticeDays: item.minimumNoticeDays,
      allowHalfDay: item.allowHalfDay,
      isDefault: item.isDefault,
      isActive: CONFIGURATION_RECORD_INACTIVE,
      rowVersion: item.rowVersion,
    },
  );
  return wfh(r.data.data);
}

function permissionPolicy(v: PermissionOrgPolicy | Record<string, unknown>): PermissionOrgPolicy {
  return {
    permissionConfigurationId: num(v.permissionConfigurationId) || null,
    organizationId: num(v.organizationId) || null,
    maximumPermissionsPerMonth: num(v.maximumPermissionsPerMonth),
    minimumDurationMinutes: num(v.minimumDurationMinutes),
    maximumDurationMinutes: num(v.maximumDurationMinutes),
    notes: notesText(v.notes),
    rowVersion: num(v.rowVersion) || 1,
  };
}

export async function getPermissionConfigurations(
  search = '',
  filters?: { isActive?: boolean },
) {
  const r = await apiClient.get<Envelope<Page<PermissionConfiguration>>>(
    API_ENDPOINTS.HR_POLICY_CONFIGURATIONS.PERMISSION,
    {
      params: {
        search: search.trim() || undefined,
        page: 1,
        pageSize: 100,
        ...(filters?.isActive === true || filters?.isActive === false
          ? { isActive: filters.isActive }
          : { isActive: CONFIGURATION_RECORD_ACTIVE }),
      },
    },
  );
  return { ...r.data.data, items: r.data.data.items.map(permission) };
}

export async function getPermissionOrgPolicy(): Promise<PermissionOrgPolicy> {
  const r = await apiClient.get<Envelope<PermissionOrgPolicy>>(
    API_ENDPOINTS.HR_POLICY_CONFIGURATIONS.PERMISSION_POLICY,
  );
  return permissionPolicy(r.data.data);
}

export async function savePermissionOrgPolicy(
  input: PermissionOrgPolicyInput,
): Promise<PermissionOrgPolicy> {
  const r = await apiClient.put<Envelope<PermissionOrgPolicy>>(
    API_ENDPOINTS.HR_POLICY_CONFIGURATIONS.PERMISSION_POLICY,
    input,
  );
  return permissionPolicy(r.data.data);
}

export async function createPermissionConfiguration(
  input: CreatePermissionConfigurationInput,
) {
  const r = await apiClient.post<Envelope<PermissionConfiguration>>(
    API_ENDPOINTS.HR_POLICY_CONFIGURATIONS.PERMISSION,
    input,
  );
  return permission(r.data.data);
}

export async function deactivatePermissionConfiguration(
  item: PermissionConfiguration,
) {
  const r = await apiClient.put<Envelope<PermissionConfiguration>>(
    API_ENDPOINTS.HR_POLICY_CONFIGURATIONS.UPDATE_PERMISSION(
      item.permissionPolicyId,
    ),
    {
      permissionTypeCode: item.permissionTypeCode,
      permissionTypeName: item.permissionTypeName,
      description: item.description,
      minimumDurationMinutes: item.minimumDurationMinutes,
      maximumDurationMinutes: item.maximumDurationMinutes,
      policyCode: item.policyCode,
      policyName: item.policyName,
      effectiveFrom: item.effectiveFrom,
      maximumPermissionsPerDay: item.maximumPermissionsPerDay,
      maximumPermissionsPerMonth: item.maximumPermissionsPerMonth,
      maximumPermissionsPerYear: item.maximumPermissionsPerYear,
      isDefault: item.isDefault,
      isActive: CONFIGURATION_RECORD_INACTIVE,
      rowVersion: item.rowVersion,
    },
  );
  return permission(r.data.data);
}
